import type { AxiosResponse } from 'axios';

import { ACCOUNT_API_URL, TOOLBOX_API_URL } from '~/lib/environment';
import { axios } from '~/modules/axios';
import { clearToken } from '~/modules/token';
import { toolboxApi, unwrapResult, type ApiEnvelope } from '~/modules/query/base';

/** The signed-in user as returned under `session.user` (purview, customer, name, ...). */
export interface SessionUser {
  [key: string]: unknown;
  purview?: Record<string, string[]>;
  customer?: { idNumber?: number; [key: string]: unknown };
  email?: string;
  firstName?: string;
  fullName?: string;
  imageUrl?: string;
}

/** `GET /me` result. */
export interface MeResult {
  user: SessionUser;
  [key: string]: unknown;
}

/** GET /me */
export type MeResponse = ApiEnvelope<MeResult>;

export interface LoginOptions {
  email: string;
  password: string;
}

/** Resolved by login() for a rejected credential / ambiguous account — not an error, status is 1. */
export interface LoginFailure {
  status: 1;
  message: string;
}

/** POST /sessions merged with the /me user. */
export type LoginSuccess = ApiEnvelope<{
  session: { user: SessionUser; [key: string]: unknown };
  [key: string]: unknown;
}>;

export type LoginResult = LoginSuccess | LoginFailure;

/** Shape rejected by login() / refreshSession(). */
export interface LoginError {
  status: 1;
  message: string;
}

/** GET /sessions/current */
export type RefreshResponse = ApiEnvelope<unknown>;

const INVALID_CREDENTIALS = 'The email or password is incorrect. Check them and try again.';

const MULTIPLE_ACCOUNTS = 'This user has multiple accounts, and cannot login at this time.';

// Shown on a 403 from /me. The service's reply helper always puts the generic reason phrase
// ("Forbidden") in data.message; the gate's own text only lands in data.detail, and this
// friendlier copy is preferred over parsing that.
const NOT_AUTHORIZED =
  'User account is not authorized to login to this application. Please contact an administrator to grant this user account login access.';

// Server-confirmed "is this session actually a Toolbox member" check (service/lib/access.js
// is the single source of truth). The global preHandler gate on the service 403s this call
// for any non-member session. Same 180s ceiling as the account calls, so a hung service
// fails the login instead of leaving the spinner up forever.
const fetchMe = async (): Promise<MeResponse> =>
  (await axios.get(`${TOOLBOX_API_URL}/me`, { timeout: 180000 })).data;

// Matches the error shape `axiosBaseQuery` produces, so RTK Query accepts it as the
// mutation's error value.
const toQueryError = (error: unknown) =>
  axios.isAxiosError(error)
    ? {
        status: error.response?.status,
        data: error.response?.data ?? { message: error.message },
        message: error.message,
      }
    : { message: error instanceof Error ? error.message : 'Request failed' };

type QueryFailure = ReturnType<typeof toQueryError>;

/** A 403 from /me means the session exists but is not a Toolbox member: drop it and say so. */
const forbidden = (): QueryFailure => {
  clearToken();

  const body: LoginError = { status: 1, message: NOT_AUTHORIZED };

  return { status: 403, data: body, message: NOT_AUTHORIZED };
};

const loginRequest = async (
  opts: LoginOptions,
): Promise<{ data: LoginResult } | { error: QueryFailure }> => {
  try {
    const accounts = await axios({
      url: `${ACCOUNT_API_URL}/GET/logins/AUTHZ/users`,
      method: 'POST',
      data: { username: opts.email, password: opts.password },
      timeout: 180000,
    });

    if (accounts.data.result.length === 0) {
      return { data: { status: 1, message: INVALID_CREDENTIALS } };
    }

    if (accounts.data.result.length > 1) {
      return { data: { status: 1, message: MULTIPLE_ACCOUNTS } };
    }

    const session: AxiosResponse = await axios({
      url: `${ACCOUNT_API_URL}/sessions`,
      method: 'POST',
      data: { user: accounts.data.result[0]._id, password: opts.password },
      timeout: 180000,
    });

    // The response interceptor has already stashed the session token by now, so fetchMe()
    // carries the Bearer header. The server (not this client's own JWT decode) is the one
    // deciding Toolbox membership -- see service/lib/access.js.
    const meResp = await fetchMe();

    return {
      data: {
        ...session.data,
        result: {
          ...session.data.result,
          session: {
            ...session.data.result.session,
            user: meResp.result.user,
          },
        },
      },
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        return { data: { status: 1, message: INVALID_CREDENTIALS } };
      }

      if (error.response?.status === 403) {
        return { error: forbidden() };
      }
    }

    return { error: toQueryError(error) };
  }
};

export const authApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResult, LoginOptions>({
      queryFn: loginRequest,
      invalidatesTags: ['Auth'],
    }),

    // Same server-side membership gate as login(); the refreshed token has already been
    // stored by the response interceptor, so fetchMe() carries it.
    refreshSession: builder.mutation<RefreshResponse, void>({
      queryFn: async (): Promise<{ data: RefreshResponse } | { error: QueryFailure }> => {
        try {
          const response = await axios({
            url: `${ACCOUNT_API_URL}/sessions/current`,
            method: 'GET',
            withCredentials: true,
            timeout: 180000,
          });

          await fetchMe();

          return { data: response.data };
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 403) {
            return { error: forbidden() };
          }

          return { error: toQueryError(error) };
        }
      },
      invalidatesTags: ['Auth'],
    }),

    me: builder.query<MeResult, void>({
      // The base query prefixes TOOLBOX_API_URL (the absolute fetchMe() above cannot).
      query: () => ({ url: '/me', method: 'GET', timeout: 180000 }),
      transformResponse: unwrapResult<MeResult>,
      providesTags: ['Auth'],
    }),
  }),
});

export const { useLoginMutation, useRefreshSessionMutation, useMeQuery } = authApi;
